import {
  ApplicationAttachmentsDocument,
  AttachmentMetaDocument,
  AttachmentUploadConfigurationDocument,
  BasicAccountApplicationAttachmentsDocument,
  CardApplicationAttachmentsDocument,
} from "../../api/commercial/generated-types";
import { ErrorBag } from "./uploader";

/**
 * This function determines whether the upload page have something uploaded (is dirty) or the current state is
 * a page that has no uploaded documents added.
 *
 * @param applicationAttachmentsDocument The attachments stored on the backend for the current user.
 * @return boolean The upload dirty state.
 */
const isUploadDirty = (
  applicationAttachmentsDocument: ApplicationAttachmentsDocument,
): boolean => {
  const isAttachmentPresentForCa =
    applicationAttachmentsDocument.cardApplicationAttachmentsDocument.find(
      (
        cardApplicationAttachmentDocument: CardApplicationAttachmentsDocument,
      ) => {
        return Boolean(
          cardApplicationAttachmentDocument.attachmentMetaDocument,
        );
      },
    );

  if (isAttachmentPresentForCa) {
    return true;
  }

  if (
    applicationAttachmentsDocument.basicAccountApplicationAttachmentsDocument ===
      null ||
    applicationAttachmentsDocument.basicAccountApplicationAttachmentsDocument ===
      undefined
  ) {
    return false;
  }

  const isAttachmentPresentForBaa =
    applicationAttachmentsDocument.basicAccountApplicationAttachmentsDocument.find(
      (baaAttachment: BasicAccountApplicationAttachmentsDocument) => {
        return Boolean(baaAttachment.attachmentMetaDocument);
      },
    );

  if (isAttachmentPresentForBaa) {
    return true;
  }
  return false;
};

const isFileTypeUploaded = (
  fileType:
    | BasicAccountApplicationAttachmentsDocument["attachmentMetaDocument"]["attachmentDocumentType"]
    | CardApplicationAttachmentsDocument["attachmentDocumentType"],
  uploadedState: ApplicationAttachmentsDocument | undefined,
): boolean => {
  if (!uploadedState) {
    return false;
  }

  const isFilePresentInBaa = Boolean(
    uploadedState?.basicAccountApplicationAttachmentsDocument?.find(
      (
        baaAttachmentDoc: BasicAccountApplicationAttachmentsDocument,
      ): boolean => {
        return (
          baaAttachmentDoc.attachmentDocumentType === fileType &&
          baaAttachmentDoc.attachmentMetaDocument !== null
        );
      },
    ),
  );

  const isFilePresentInCa = Boolean(
    uploadedState?.cardApplicationAttachmentsDocument?.find(
      (caAttachmentDoc: CardApplicationAttachmentsDocument): boolean => {
        return (
          caAttachmentDoc.attachmentDocumentType === fileType &&
          caAttachmentDoc.attachmentMetaDocument !== null
        );
      },
    ),
  );

  return isFilePresentInBaa || isFilePresentInCa;
};

/**
 * This function determines whether the adapter is ready to render the uploader(s) based on the response from the server.
 *
 * @param attachmentProperties The settings for the attachments. Contains meta information like max file size.
 * @param applicationAttachments The attachments stored on the backend for the current user.
 */
const areAttachmentsReadyForRender = (
  attachmentProperties: AttachmentUploadConfigurationDocument | undefined,
  applicationAttachments: ApplicationAttachmentsDocument | undefined,
): boolean => {
  return (
    Boolean(attachmentProperties) !== false &&
    Boolean(applicationAttachments) !== false
  );
};

/**
 * This function takes the file size provided from backend in KB and transforms it into MB.
 *
 * @param fileSizeInBytes The file size in Bytes.
 */
const calculateFileSizeInMB = (fileSizeInBytes: number): number => {
  return fileSizeInBytes / (1024 * 1024);
};

/**
 * Type guard for upload state.
 *
 * @param uploadStatus The upload state.
 */
function isUploadFailed(
  uploadStatus: AttachmentMetaDocument | ErrorBag | undefined,
): uploadStatus is ErrorBag {
  return (uploadStatus as ErrorBag)?.error !== undefined;
}

/**
 * Type guard for upload state.
 *
 * @param uploadStatus The upload state.
 */
function isUploadSuccessful(
  uploadStatus: AttachmentMetaDocument | ErrorBag | undefined,
): uploadStatus is AttachmentMetaDocument {
  return (uploadStatus as AttachmentMetaDocument)?.uuid !== undefined;
}

export const UploadHelper = {
  isUploadDirty,
  areAttachmentsReadyForRender,
  calculateFileSizeInMB,
  isFileTypeUploaded,
  isUploadSuccessful,
  isUploadFailed,
};
